import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import {
  GoogleMap,
  useJsApiLoader,
  OverlayViewF,
  OVERLAY_MOUSE_TARGET,
} from '@react-google-maps/api';
import { ClipLoader, BeatLoader } from 'react-spinners';
import {
  MapPinIcon,
  ChevronLeftIcon,
  ListBulletIcon,
  Squares2X2Icon,
  BuildingStorefrontIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { MapIcon } from '@heroicons/react/24/solid';

import { getAllEvents } from '../../redux/actions/events';
import getEventStatus, { isEventCancelled } from 'utils/eventStatus';
import applyEventTimeFilters from 'utils/eventTimeFilters';
import { getEventDateRange, getTodayDateOnly } from 'utils/eventDateRange';
import normalizeEventDate from 'utils/normalizeEventDate';
import { useMediaQuery } from 'hooks/useMediaQuery';

import EventSearchBar from 'components/EventSearchBar';
import CategoryList from 'components/CategoryList';

import './mapStyle.css';
import '../../index.css';

const MAP_VERSION = 'quarterly';
const initialCenter = { lat: 38.736946, lng: -9.142685 };

const EventImage = ({ src, alt = '', iconClassName = 'w-10 h-10' }) => {
  if (src) {
    return (
      <img
        src={src}
        alt={alt}
        className="absolute inset-0 w-full h-full object-cover"
        loading="lazy"
      />
    );
  }
  return (
    <div className="absolute inset-0 grid place-items-center bg-paper">
      <BuildingStorefrontIcon className={`${iconClassName} text-gray-300`} />
    </div>
  );
};

// ─── Map style: muted paper / amber-Mapbox light ────────────────────────────
const PAPER_MAP_STYLES = [
  { elementType: 'geometry', stylers: [{ color: '#ECE7DD' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#AC9F8A' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#F4F0E7' }] },
  {
    featureType: 'administrative',
    elementType: 'geometry',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'administrative.land_parcel',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'administrative.neighborhood',
    stylers: [{ visibility: 'off' }],
  },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  {
    featureType: 'poi.park',
    elementType: 'geometry',
    stylers: [{ color: '#CFDDBF' }],
  },
  {
    featureType: 'poi.park',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#7E8C5D' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ color: '#FBF8F1' }],
  },
  {
    featureType: 'road',
    elementType: 'labels',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'road.arterial',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#E0D9CA' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [{ color: '#F0EADB' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#D8CFBE' }],
  },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#A9CEDA' }],
  },
  {
    featureType: 'water',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#7FA9B6' }],
  },
];

// ─── Helpers ────────────────────────────────────────────────────────────────
const cx = (...a) => a.filter(Boolean).join(' ');

const getEventCoords = (e) => {
  if (!e) return null;
  let lat, lng;
  if (e.locationCoords) {
    lat = parseFloat(e.locationCoords.lat);
    lng = parseFloat(e.locationCoords.lng);
  } else if (e.lat !== undefined && e.lng !== undefined) {
    lat = parseFloat(e.lat);
    lng = parseFloat(e.lng);
  }
  if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  return null;
};

const hasValidCoords = (e) => getEventCoords(e) !== null;

const fmtEUR = (n) =>
  n == null ? '' : `€${Math.round(Number(n)).toLocaleString('en-GB')}`;

const stripDots = (s) => (typeof s === 'string' ? s.replaceAll('.', '') : s);
const fmtMonthShort = (d, locale) =>
  stripDots(moment(d).locale(locale).format('MMM')).toUpperCase();

const getDateInfo = (rawDate, locale) => {
  const range = getEventDateRange(rawDate);
  if (!range) return null;
  const date = normalizeEventDate(rawDate);
  const s = moment(range.startDate);
  const e = moment(range.endDate);
  const sameMonth = s.year() === e.year() && s.month() === e.month();
  const label = sameMonth
    ? `${s.date()}–${e.date()} ${fmtMonthShort(s, locale)} ${s.year()}`
    : `${s.date()} ${fmtMonthShort(s, locale)} – ${e.date()} ${fmtMonthShort(e, locale)} ${s.year()}`;
  let days;
  if (date?.type === 'segmented' && Array.isArray(date.days)) {
    days = date.days.filter((d) => moment(d?.date).isValid()).length;
  } else {
    days = Math.max(e.diff(s, 'days') + 1, 1);
  }
  return { label, days };
};

const fillState = (current, total, t) => {
  const c = Number(current) || 0;
  const tot = Number(total) || 0;
  const pct = tot > 0 ? Math.min((c / tot) * 100, 100) : 0;
  if (tot === 0 || c === 0)
    return { pct, label: t('listing.fill_spaces_open'), tone: 'open' };
  if (pct >= 100)
    return { pct: 100, label: t('listing.fill_sold_out'), tone: 'sold' };
  if (pct >= 80)
    return { pct, label: t('listing.fill_almost_full'), tone: 'warn' };
  if (pct > 50)
    return { pct, label: t('listing.fill_filling_up'), tone: 'warn' };
  return { pct, label: t('listing.fill_spaces_open'), tone: 'open' };
};

// ─── Atoms ──────────────────────────────────────────────────────────────────
const StateBadge = ({ event, className }) => {
  const { t } = useTranslation();
  const status = getEventStatus(event?.date);
  if (status === 'live' || event?.state === 'live') {
    return (
      <span
        className={cx(
          'inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 text-[10px] font-bold uppercase tracking-[0.08em]',
          className
        )}
      >
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
        {t('listing.event_status_live')}
      </span>
    );
  }
  if (status === 'finished') {
    return (
      <span
        className={cx(
          'inline-flex items-center px-2 py-1 rounded-full bg-paper text-gray-500 ring-1 ring-line text-[10px] font-bold uppercase tracking-[0.08em]',
          className
        )}
      >
        {t('listing.event_status_past')}
      </span>
    );
  }
  const range = getEventDateRange(event?.date);
  if (!range) return null;
  const today = getTodayDateOnly();
  const days = moment(range.startDate, 'YYYY-MM-DD').diff(
    moment(today, 'YYYY-MM-DD'),
    'days'
  );
  if (days > 0 && days <= 14) {
    return (
      <span
        className={cx(
          'inline-flex items-center px-2 py-1 rounded-full bg-brand-soft text-amber-900 ring-1 ring-amber-200 text-[10px] font-bold uppercase tracking-[0.08em]',
          className
        )}
      >
        {t('listing.event_status_in_days', { count: days })}
      </span>
    );
  }
  return null;
};

const FillBar = ({ current, total, dense = false }) => {
  const { t } = useTranslation();
  const s = fillState(current, total, t);
  const bar =
    s.tone === 'sold'
      ? 'bg-rose-500'
      : s.tone === 'warn'
        ? 'bg-amber-500'
        : 'bg-emerald-500';
  const labelColor =
    s.tone === 'sold'
      ? 'text-rose-600'
      : s.tone === 'warn'
        ? 'text-amber-700'
        : 'text-emerald-700';
  const safeCurrent = Number(current) || 0;
  const safeTotal = Number(total) || 0;
  return (
    <div className="w-full">
      <div
        className={cx(
          'flex items-center justify-between gap-2',
          dense ? 'mb-1' : 'mb-1.5'
        )}
      >
        <span
          className={cx(
            'text-[10px] uppercase tracking-[0.1em] font-bold whitespace-nowrap',
            labelColor
          )}
        >
          {s.label}
        </span>
        {safeTotal > 0 && (
          <span className="text-[11px] font-bold tabular-nums text-gray-700">
            {safeCurrent}
            <span className="text-gray-400 font-medium">/{safeTotal}</span>
          </span>
        )}
      </div>
      <div
        className={cx(
          'w-full rounded-full overflow-hidden bg-paper ring-1 ring-line',
          dense ? 'h-1' : 'h-1.5'
        )}
      >
        <div
          className={cx('h-full rounded-full transition-all', bar)}
          style={{ width: `${Math.max(s.pct, safeCurrent > 0 ? 4 : 0)}%` }}
        />
      </div>
    </div>
  );
};

const PriceChip = ({ price, size = 'md', variant = 'amber' }) => {
  const { t } = useTranslation();
  if (price == null) return null;
  const sz =
    size === 'sm'
      ? 'px-2.5 py-0.5 text-[11px]'
      : size === 'lg'
        ? 'px-3.5 py-1.5 text-[14px]'
        : 'px-3 py-1 text-[12px]';
  const tones = {
    amber: 'bg-brand-soft text-ink-strong ring-1 ring-amber-200',
    ink: 'bg-ink-strong text-white',
    outline: 'bg-white text-ink-strong ring-1 ring-line',
  };
  return (
    <div
      className={cx(
        'inline-flex items-baseline gap-1 rounded-full font-bold tabular-nums',
        sz,
        tones[variant]
      )}
    >
      <span className="text-[9px] uppercase tracking-[0.12em] font-bold opacity-70 -translate-y-px">
        {t('common.from_price')}
      </span>
      <span>{fmtEUR(price)}</span>
    </div>
  );
};

// ─── Price-bubble marker (white / amber-hover / ink-selected) ───────────────
const PriceBubbleMarker = ({
  event,
  isSelected,
  isHovered,
  isDimmed,
  onClick,
  onEnter,
  onLeave,
  isAuthenticated,
}) => {
  const coords = getEventCoords(event);
  if (!coords) return null;
  // Prices, like availability, are signed-in only: a guest's marker is a plain
  // pin instead of a "from €X" bubble, so the map still reads the same.
  const label = isAuthenticated ? fmtEUR(event?.lowestPrice) : null;

  const toneBubble = isSelected
    ? 'bg-ink-strong text-white ring-2 ring-white shadow-lg'
    : isHovered
      ? 'bg-brand text-ink-strong ring-2 ring-white shadow-lg'
      : 'bg-white text-ink-strong ring-1 ring-black/10 shadow-md';

  const toneTail = isSelected
    ? 'bg-ink-strong'
    : isHovered
      ? 'bg-brand'
      : 'bg-white ring-1 ring-black/10';

  return (
    <OverlayViewF position={coords} mapPaneName={OVERLAY_MOUSE_TARGET}>
      <div
        className="price-bubble-marker"
        style={{
          transform: 'translate(-50%, -50%)',
          opacity: isDimmed ? 0.45 : 1,
          transition: 'opacity .25s, transform .18s',
          zIndex: isSelected ? 40 : isHovered ? 30 : 10,
        }}
      >
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClick?.();
          }}
          onMouseEnter={onEnter}
          onMouseLeave={onLeave}
          className={cx(
            'relative inline-flex items-center justify-center rounded-full font-extrabold tabular-nums whitespace-nowrap',
            'focus:outline-none transition-transform',
            label ? 'px-3 py-1.5 text-[13px] leading-none' : 'w-5 h-5',
            toneBubble,
            isSelected
              ? 'scale-110'
              : isHovered
                ? 'scale-105'
                : 'hover:scale-105'
          )}
        >
          {label ?? (
            <span className="w-2 h-2 rounded-full bg-current opacity-70" />
          )}
          <span
            className={cx(
              'absolute left-1/2 -translate-x-1/2 top-full w-2 h-2 rotate-45 -mt-1 rounded-[1px]',
              toneTail
            )}
          />
        </button>
      </div>
    </OverlayViewF>
  );
};

// ─── Compact icon preview, anchored above the selected marker ───────────────
// The full event details already live in the side panel (desktop) and bottom
// sheet (mobile), so the on-map popup is intentionally just a small, discreet
// event icon rather than a large card that smothers the map (T2B-355).
const MapPreviewMini = ({ event, onView, t }) => (
  <button
    type="button"
    onClick={(e) => {
      e.stopPropagation();
      onView?.();
    }}
    className="relative block w-14 h-14 rounded-2xl overflow-hidden ring-2 ring-white shadow-xl bg-paper hover:scale-105 transition-transform focus:outline-none"
    aria-label={t('listing.view_event', 'View event')}
    title={event?.name || ''}
  >
    <EventImage
      src={event.landscapeImage || event.avatar}
      alt={event.name || ''}
      iconClassName="w-7 h-7"
    />
    <span className="absolute left-1/2 -translate-x-1/2 top-full w-2.5 h-2.5 rotate-45 -mt-[5px] rounded-[2px] bg-white" />
  </button>
);

// ─── Compact list card for the floating panel ──────────────────────────────
const MapListCard = ({
  event,
  selected,
  hovered,
  onClick,
  onEnter,
  onLeave,
  onView,
  refCb,
  isAuthenticated,
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n?.language || 'en';
  const dateInfo = getDateInfo(event.date, locale);
  const locationLabel = [event?.area, event?.city].filter(Boolean).join(' · ');

  // Same rule as the listing cards: an event that is running, over or called
  // off has nothing left to book, so it advertises neither how full it is nor
  // what it costs. Past events can still show up here when the user turns the
  // "hide past events" filter off.
  const dateStatus = getEventStatus(event?.date);
  const isLive = dateStatus === 'live' || event?.state === 'live';
  const isClosed =
    isLive || dateStatus === 'finished' || isEventCancelled(event);

  return (
    <div
      ref={refCb}
      data-event-id={event.id}
      onClick={onClick}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      className={cx(
        'group relative bg-white rounded-2xl p-2.5 flex gap-3 cursor-pointer transition shadow-card',
        // Every block below reserves its height, so the cards in the panel
        // line up at the same height whatever each event carries.
        isAuthenticated ? 'min-h-[140px]' : 'min-h-[120px]',
        selected
          ? 'ring-2 ring-brand'
          : hovered
            ? 'ring-1 ring-brand/50'
            : 'ring-1 ring-line hover:ring-gray-300'
      )}
    >
      <div className="relative w-[96px] h-[96px] rounded-xl overflow-hidden ring-1 ring-line shrink-0 bg-paper">
        <EventImage
          src={event.landscapeImage || event.avatar}
          iconClassName="w-8 h-8"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
        {/* The panel card selects a marker; this is the way out of the map and
            into the event itself. */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onView?.();
          }}
          className="absolute left-1.5 bottom-1.5 w-7 h-7 grid place-items-center rounded-full bg-white/95 backdrop-blur text-ink-strong shadow-sm ring-1 ring-black/5 hover:bg-white hover:scale-105 transition focus:outline-none"
          aria-label={t('listing.view_event', 'View event')}
          title={t('listing.view_event', 'View event')}
        >
          <EyeIcon className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 min-w-0 flex flex-col">
        <div className="flex items-start gap-2 min-h-[40px]">
          <h3 className="flex-1 font-extrabold text-[14.5px] text-ink-strong leading-snug line-clamp-2 tracking-tight">
            {event.name}
          </h3>
          <StateBadge event={event} />
        </div>
        <p className="mt-0.5 text-[11.5px] text-gray-500 flex items-center gap-1 truncate">
          <MapPinIcon className="w-3 h-3 shrink-0" />
          <span className="truncate">
            {[locationLabel, dateInfo?.label].filter(Boolean).join(' · ')}
          </span>
        </p>

        <div className="mt-1.5 flex items-center gap-1.5 min-h-[20px]">
          {!!event.categoriesAllowed?.length && (
            <CategoryList
              categories={event.categoriesAllowed}
              onlyMacro
              withLabel
              macroIconClass="w-5 h-5"
              containerClass="flex flex-row flex-wrap items-center gap-1.5 overflow-visible"
            />
          )}
        </div>

        <div className="mt-auto pt-2 flex items-end justify-between gap-3">
          <div className="flex-1 min-w-0 max-w-[200px]">
            {/* Availability is signed-in only, like the listing cards: a guest
                sees the event, not how full it is. */}
            {isAuthenticated && !isClosed && (
              <FillBar
                current={event.bookedSpaces}
                total={event.totalSpaces}
                dense
              />
            )}
          </div>
          {isAuthenticated && !isClosed && (
            <PriceChip price={event.lowestPrice} variant="amber" size="sm" />
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Skeleton / empty for the panel ─────────────────────────────────────────
const SkeletonRow = () => (
  <div className="bg-white rounded-2xl p-2.5 flex gap-3 ring-1 ring-line shadow-card">
    <div className="w-[96px] h-[96px] rounded-xl bg-gray-200 animate-pulse" />
    <div className="flex-1 min-w-0">
      <div className="h-4 w-3/4 rounded bg-gray-200 animate-pulse mb-2" />
      <div className="h-3 w-1/2 rounded bg-gray-200 animate-pulse mb-3" />
      <div className="flex gap-1.5">
        <div className="h-5 w-5 rounded bg-gray-200 animate-pulse" />
        <div className="h-5 w-5 rounded bg-gray-200 animate-pulse" />
      </div>
    </div>
  </div>
);

const EmptyPanel = ({ message }) => (
  <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
    <div className="w-12 h-12 rounded-full bg-paper grid place-items-center mb-3">
      <MapPinIcon className="w-6 h-6 text-gray-400" />
    </div>
    <p className="text-gray-500 text-sm font-medium">{message}</p>
  </div>
);

// ─── List ↔ Map mode toggle ────────────────────────────────────────────────
const MapModeToggle = ({ onShowList }) => {
  const { t } = useTranslation();
  return (
    <div className="inline-flex items-center gap-0.5 bg-white/95 backdrop-blur rounded-full p-0.5 ring-1 ring-black/10 shadow-md">
      <button
        type="button"
        onClick={onShowList}
        className="px-3 py-1.5 rounded-full text-[12px] font-bold text-gray-500 hover:text-ink-strong inline-flex items-center gap-1.5 transition"
      >
        <Squares2X2Icon className="w-3.5 h-3.5" />
        {t('listing.list_button', 'List')}
      </button>
      <span className="px-3 py-1.5 rounded-full text-[12px] font-bold bg-yellow-03 text-white shadow-sm inline-flex items-center gap-1.5">
        <MapIcon className="w-3.5 h-3.5" />
        {t('listing.map_button', 'Map')}
      </span>
    </div>
  );
};

// ─── Page ───────────────────────────────────────────────────────────────────
const EventListMap = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const listContainerRef = useRef(null);
  const cardRefs = useRef({});
  const hasFittedBoundsRef = useRef(false);
  const prevEventsKeyRef = useRef('');

  const { events = [], loading: loadingEvents = false } = useSelector(
    (state) => state.events
  );
  const { isAuthenticated } = useSelector((state) => state.auth);

  // Mirrors the listing's defaults: past (and cancelled) events are hidden
  // until the user says otherwise, live ones are shown. EventSearchBar reports
  // the filters it searched with so the panel and the markers hide exactly
  // what the listing would hide.
  const [timeFilters, setTimeFilters] = useState({
    hidePastEvents: true,
    hideLiveEvents: false,
  });
  const timeFiltersRef = useRef(timeFilters);
  timeFiltersRef.current = timeFilters;

  const validEvents = useMemo(
    () => applyEventTimeFilters(events, timeFilters).filter(hasValidCoords),
    [events, timeFilters]
  );
  const liveCount = useMemo(
    () =>
      validEvents.filter(
        (e) => e?.state === 'live' || getEventStatus(e?.date) === 'live'
      ).length,
    [validEvents]
  );

  const isMobile = useMediaQuery('(max-width: 768px)');

  // The map instance is state, not a ref: a ref assignment re-renders nothing,
  // so effects that need the map would never re-run once it finally loaded —
  // which is how the bottom sheet ended up empty and the initial fit sometimes
  // never happened. Holding the instance itself (rather than a ready flag)
  // also re-runs them when the map is recreated and a fresh instance arrives.
  const [map, setMap] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [hoveredEventId, setHoveredEventId] = useState(null);
  const [panelOpen, setPanelOpen] = useState(true);
  const [sheetExpanded, setSheetExpanded] = useState(false);

  // Fetch once on mount + when language changes
  useEffect(() => {
    dispatch(
      getAllEvents({
        page: 1,
        pageSize: 50,
        append: false,
        lang: i18n.language,
        // Read through the ref: a filter change is already served by the
        // search bar's own request, so it must not trigger a second one here.
        ...timeFiltersRef.current,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  // Auto-select the first event. Deliberately independent of the map: the
  // desktop panel and the mobile bottom sheet both render off `selectedEvent`,
  // so gating this on the map instance left the sheet showing its empty state
  // whenever the events arrived before Google Maps finished loading.
  useEffect(() => {
    if (validEvents.length === 0) return;
    setSelectedEvent((current) =>
      current && validEvents.some((e) => e.id === current.id)
        ? current
        : validEvents[0]
    );
  }, [validEvents]);

  // Initial fit, once there is a map to fit. Clicking the map clears the
  // selection, so this must not re-run on `selectedEvent` — the key/fitted
  // guard is what keeps a deselect from bouncing straight back.
  useEffect(() => {
    if (!map || validEvents.length === 0) return;
    const eventsKey = validEvents.map((e) => e.id).join(',');
    if (eventsKey === prevEventsKeyRef.current && hasFittedBoundsRef.current)
      return;
    prevEventsKeyRef.current = eventsKey;

    if (window.google) {
      try {
        const bounds = new window.google.maps.LatLngBounds();
        let n = 0;
        validEvents.forEach((ev) => {
          const c = getEventCoords(ev);
          if (c) {
            bounds.extend(c);
            n++;
          }
        });
        if (n > 1 && !bounds.isEmpty()) {
          map.fitBounds(bounds, { padding: 80 });
        } else if (n === 1) {
          const c = getEventCoords(validEvents[0]);
          if (c) {
            map.setCenter(c);
            map.setZoom(15);
          }
        }
        hasFittedBoundsRef.current = true;
      } catch (err) {
        hasFittedBoundsRef.current = true;
      }
    }
  }, [map, validEvents]);

  // Pan to selected, biased so marker sits below centre (room for popup above).
  useEffect(() => {
    const coords = getEventCoords(selectedEvent);
    if (!coords || !map) return;
    const z = map.getZoom();
    map.panTo(coords);
    if (z != null && z < 13) map.setZoom(14);
    // Push the center upward so the marker lands a touch below midline,
    // leaving room for the upward popup.
    requestAnimationFrame(() => {
      try {
        map.panBy(0, -110);
      } catch {}
    });

    // Scroll the matching card into view in the panel (container.scrollTo).
    const el = cardRefs.current[selectedEvent?.id];
    const c = listContainerRef.current;
    if (el && c) {
      c.scrollTo({
        top: Math.max(0, (el.offsetTop ?? 0) - 12),
        behavior: 'smooth',
      });
    }
  }, [selectedEvent, map]);

  const loaderOptions = useMemo(
    () => ({
      googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
      id: 'google-map-script',
      version: MAP_VERSION,
      libraries: ['places'],
    }),
    []
  );

  const { isLoaded, loadError } = useJsApiLoader(loaderOptions);

  const onMapLoad = useCallback((mapInstance) => {
    hasFittedBoundsRef.current = false;
    setMap(mapInstance);
  }, []);

  const handleSelect = useCallback((ev) => {
    if (!ev) return;
    setSelectedEvent(ev);
    if (window.matchMedia && window.matchMedia('(max-width: 768px)').matches) {
      setSheetExpanded(false);
    }
  }, []);

  const handleMarkerClick = useCallback(
    (id) => {
      const ev = validEvents.find((e) => e.id === id);
      if (ev) handleSelect(ev);
    },
    [validEvents, handleSelect]
  );

  const containerStyle = useMemo(() => ({ width: '100%', height: '100%' }), []);

  const mapOptions = useMemo(
    () => ({
      clickableIcons: false,
      streetViewControl: false,
      mapTypeControl: false,
      fullscreenControl: false,
      zoomControl: true,
      gestureHandling: 'greedy',
      styles: PAPER_MAP_STYLES,
      zoomControlOptions: window.google
        ? { position: window.google.maps.ControlPosition.RIGHT_CENTER }
        : undefined,
    }),
    []
  );

  if (loadError) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-red-500">{t('listing.error_map')}</p>
      </div>
    );
  }

  const isInitialLoading = loadingEvents && validEvents.length === 0;
  const isRefreshing = loadingEvents && validEvents.length > 0;

  const renderMarkers = () =>
    isLoaded && window.google
      ? validEvents.map((ev) => (
          <PriceBubbleMarker
            key={ev.id}
            event={ev}
            isAuthenticated={isAuthenticated}
            isSelected={selectedEvent?.id === ev.id}
            isHovered={hoveredEventId === ev.id}
            isDimmed={
              hoveredEventId != null &&
              hoveredEventId !== ev.id &&
              selectedEvent?.id !== ev.id
            }
            onClick={() => handleMarkerClick(ev.id)}
            onEnter={() => setHoveredEventId(ev.id)}
            onLeave={() => setHoveredEventId(null)}
          />
        ))
      : null;

  const renderPopup = () => {
    const ev =
      selectedEvent && validEvents.find((e) => e.id === selectedEvent.id);
    const coords = getEventCoords(ev);
    if (!ev || !coords || !isLoaded) return null;
    return (
      <OverlayViewF position={coords} mapPaneName="floatPane">
        <div
          style={{
            transform: 'translate(-50%, calc(-100% - 16px))',
            zIndex: 50,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <MapPreviewMini
            event={ev}
            t={t}
            onView={() => navigate(`/event/${ev.slug}`)}
          />
        </div>
      </OverlayViewF>
    );
  };

  // ─── Panel content (used for desktop side panel + mobile bottom sheet) ──
  const PanelHeader = (
    <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-line/80">
      <div className="flex items-center gap-2 text-[12.5px] text-gray-500 min-w-0">
        <span className="truncate font-bold text-ink-strong">
          {t('common.events')}
        </span>
        {liveCount > 0 && (
          <span className="hidden sm:inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 text-[10.5px] font-bold uppercase tracking-[0.06em]">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            {liveCount} {t('listing.event_status_live', 'live')}
          </span>
        )}
      </div>
    </div>
  );

  const PanelList = (
    <div
      ref={listContainerRef}
      className="relative flex-1 overflow-y-auto event-list-scroll p-3 space-y-2.5"
    >
      {isRefreshing && (
        <div className="absolute inset-0 z-10 pointer-events-none">
          <div className="absolute inset-0 bg-white/60 backdrop-blur-sm" />
          <div className="absolute inset-0 flex items-start justify-center pt-6">
            <div className="inline-flex items-center gap-3 rounded-full bg-white/95 shadow-lg ring-1 ring-black/5 px-4 py-2">
              <BeatLoader size={6} color="#FFBD59" />
              <span className="text-xs font-medium text-gray-700">
                {t('listing.updating', 'Updating…')}
              </span>
            </div>
          </div>
        </div>
      )}

      {isInitialLoading ? (
        Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)
      ) : validEvents.length === 0 ? (
        <EmptyPanel
          message={t(
            'listing.no_events_with_location',
            'No events with a location'
          )}
        />
      ) : (
        validEvents.map((ev) => (
          <MapListCard
            key={ev.id}
            event={ev}
            isAuthenticated={isAuthenticated}
            refCb={(el) => {
              cardRefs.current[ev.id] = el;
            }}
            selected={selectedEvent?.id === ev.id}
            hovered={hoveredEventId === ev.id}
            onClick={() => handleSelect(ev)}
            onView={() => navigate(`/event/${ev.slug}`)}
            onEnter={() => setHoveredEventId(ev.id)}
            onLeave={() => setHoveredEventId(null)}
          />
        ))
      )}
    </div>
  );

  // ─── Render ─────────────────────────────────────────────────────────────
  return (
    <div className="event-list-map-container relative w-full h-screen bg-paper-bg overflow-hidden">
      {/* Full-bleed map */}
      <div className="absolute inset-0">
        {isLoaded ? (
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={initialCenter}
            zoom={12}
            onLoad={onMapLoad}
            options={mapOptions}
            onClick={() => setSelectedEvent(null)}
          >
            {renderMarkers()}
            {renderPopup()}
          </GoogleMap>
        ) : (
          <div className="flex justify-center items-center h-full">
            <ClipLoader color="#FFBD59" loading={true} size={60} />
          </div>
        )}
      </div>

      {/* Floating search bar */}
      <div className="absolute top-4 left-4 right-4 md:left-[510px] md:right-[200px] z-30 pointer-events-none flex justify-center">
        <div className="pointer-events-auto w-full max-w-3xl">
          <EventSearchBar
            currentSearch="events"
            isAuthenticated={isAuthenticated}
            onEventFiltersChange={setTimeFilters}
          />
        </div>
      </div>

      {/* Top-right: mode toggle */}
      <div className="absolute top-4 right-4 z-30 hidden md:block">
        <MapModeToggle onShowList={() => navigate('/listings')} />
      </div>

      {/* DESKTOP — floating glass panel */}
      {!isMobile && (
        <div
          className="absolute top-[84px] bottom-4 left-4 w-[470px] max-w-[80vw] z-20 transition-transform duration-300"
          style={{
            transform: panelOpen
              ? 'translateX(0)'
              : 'translateX(calc(-100% - 16px))',
          }}
        >
          <div className="h-full flex flex-col rounded-2xl bg-white/92 backdrop-blur-md ring-1 ring-black/10 shadow-2xl overflow-hidden">
            {PanelHeader}
            {PanelList}
          </div>

          <button
            type="button"
            onClick={() => setPanelOpen((o) => !o)}
            className="absolute top-3 -right-3 translate-x-full w-9 h-9 grid place-items-center rounded-full bg-yellow-03 text-white shadow-lg hover:brightness-110 transition"
            title={
              panelOpen
                ? t('map.hide_list', 'Hide list')
                : t('map.show_list', 'Show list')
            }
          >
            <ChevronLeftIcon
              className="w-6 h-6"
              style={{ transform: panelOpen ? 'none' : 'rotate(180deg)' }}
            />
          </button>
        </div>
      )}

      {/* MOBILE — bottom sheet peeking the selected card */}
      {isMobile && (
        <>
          <div
            className="fixed left-0 right-0 bottom-0 z-30 transition-transform duration-300"
            style={{
              transform: sheetExpanded
                ? 'translateY(0)'
                : // Drag handle + the card's own reserved height, so the peek
                  // shows the whole card instead of clipping its last row.
                  'translateY(calc(100% - 182px))',
              height: '70vh',
            }}
          >
            <div className="h-full flex flex-col bg-white/95 backdrop-blur-md ring-1 ring-black/10 shadow-2xl rounded-t-3xl overflow-hidden">
              <button
                type="button"
                onClick={() => setSheetExpanded((v) => !v)}
                className="pt-2 pb-1 flex justify-center"
                aria-label={
                  sheetExpanded
                    ? t('map.collapse', 'Collapse')
                    : t('map.expand', 'Expand')
                }
              >
                <span className="w-10 h-1.5 rounded-full bg-line" />
              </button>
              {sheetExpanded ? (
                <>
                  {PanelHeader}
                  {PanelList}
                </>
              ) : (
                <div className="p-3">
                  {selectedEvent ? (
                    <MapListCard
                      event={selectedEvent}
                      isAuthenticated={isAuthenticated}
                      selected
                      onClick={() => setSheetExpanded(true)}
                      onView={() => navigate(`/event/${selectedEvent.slug}`)}
                    />
                  ) : (
                    <EmptyPanel
                      message={t(
                        'listing.no_events_with_location',
                        'No events with a location'
                      )}
                    />
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="fixed top-4 right-4 z-30">
            <MapModeToggle onShowList={() => navigate('/listings')} />
          </div>

          <div className="fixed bottom-[198px] left-1/2 -translate-x-1/2 z-30">
            <button
              type="button"
              onClick={() => navigate('/listings')}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-ink-strong text-white text-[13px] font-bold shadow-lg hover:bg-black transition"
            >
              <ListBulletIcon className="w-4 h-4" />
              {t('listing.list_button')}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default EventListMap;
